import { z } from 'zod';
import { IsoTime, ServerId } from './basics.js';
/**
 * Wazuh as a central service (build plan P11.8; P11.8.2 here): each monitored
 * server runs the small Wazuh *agent*, which enrols to the manager on the
 * central server and reports to it there.
 *
 * Enrolment is by password over Bartizo's own channel (W7). An enrolled, approved
 * Bartizo agent asks `GET /v1/agent/wazuh-enrollment` for the manager's address,
 * the password `wazuh-authd` wants and the certificate to verify the manager by;
 * the root helper's `enrol_wazuh_agent` writes them where the Wazuh agent reads
 * them and starts it. The Wazuh agent's name is the Bartizo server id, which is
 * how its alerts map back to the server (W9), and it enrols straight into its own
 * group, `bz-<server id>` (W5), which the API makes by creating the group's
 * directory — checked on 4.14.8: `wazuh-authd` accepts a group that exists only
 * as `etc/shared/<group>/`, and refuses one that does not exist at all.
 */
/** W5: one Wazuh group per server. */
export const WAZUH_GROUP_PREFIX = 'bz-';
export const wazuhGroupFor = (serverId) => `${WAZUH_GROUP_PREFIX}${serverId}`;
/** `bz-` and a server id (`ServerId`): characters Wazuh allows in a group's name. */
export const WazuhGroup = z
    .string()
    .max(67)
    .regex(/^bz-[a-z0-9][a-z0-9-]*$/);
/** W8: agent traffic and enrolment. Fixed, not settings: central's template opens these. */
export const WAZUH_AGENT_PORT = 1514;
export const WAZUH_ENROLLMENT_PORT = 1515;
/**
 * Where agents reach the manager: a host name or an address, written into the
 * agent's `ossec.conf` by root. Letters, digits, dots, hyphens and colons only,
 * so nothing here can close an XML element or open another.
 */
export const WazuhManagerAddress = z.union([
    z.ipv4(),
    z.ipv6(),
    z
        .string()
        .max(253)
        .regex(/^(?=.{1,253}$)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/),
]);
/**
 * `wazuh-authd`'s password. Central makes 64 hex digits (`openssl rand -hex 32`,
 * P11.8.1); anything printable without spaces or quotes is accepted, so a
 * password an administrator set by hand still works.
 */
export const WazuhEnrollmentPassword = z
    .string()
    .min(16)
    .max(128)
    .regex(/^[A-Za-z0-9._~+/=-]+$/);
/** One PEM certificate: Bartizo's own for `wazuh-authd` (P11.8.1). */
export const WazuhCaPem = z
    .string()
    .max(8192)
    .regex(/^-----BEGIN CERTIFICATE-----\r?\n(?:[A-Za-z0-9+/=]{1,76}\r?\n)+-----END CERTIFICATE-----\r?\n?$/);
/** `GET /v1/agent/wazuh-enrollment`. A secret travels in it: never logged, never stored by the agent. */
export const WazuhEnrollment = z.object({
    manager_address: WazuhManagerAddress,
    password: WazuhEnrollmentPassword,
    ca_pem: WazuhCaPem,
    /** This server's id: the Wazuh agent's name (W7, W9). */
    agent_name: ServerId,
    group: WazuhGroup,
});
/** `DELETE /v1/agent/wazuh-enrollment`: whether central had a Wazuh agent to remove. */
export const WazuhUnenrollment = z.object({ removed: z.boolean() });
/**
 * `enrol_wazuh_agent`'s arguments: the enrolment, as the helper writes it. The
 * helper also checks `group` is `agent_name`'s, so the two cannot be crossed.
 */
export const EnrolWazuhAgentArgs = WazuhEnrollment.refine((args) => args.group === wazuhGroupFor(args.agent_name), { message: "group must be the agent's own", path: ['group'] });
export const EnrolWazuhAgentResult = z.object({
    /** False when everything was already so: nothing written, nothing restarted. */
    changed: z.boolean(),
    restarted: z.boolean(),
    /** The agent has a key from the manager (`client.keys` is not empty). */
    enrolled: z.boolean(),
});
/**
 * W6: the lines that keep remote commands off, in `local_internal_options.conf`,
 * which upgrades leave alone. The package already defaults them to 0; pinning
 * them here keeps a shared `agent.conf` from ever running a command as root.
 */
export const WAZUH_REMOTE_COMMAND_LOCKS = [
    'logcollector.remote_commands=0',
    'wazuh_command.remote_commands=0',
    'sca.remote_commands=0',
];
/** `wazuh-agentd.state`'s own words, or `unknown` when there is no state file yet. */
export const WazuhAgentStatus = z.enum(['pending', 'connected', 'disconnected', 'unknown']);
/** `inspect_wazuh_agent`: read-only, no arguments. */
export const WazuhAgentInspection = z.object({
    /** `wazuh-agent` is installed (and not a manager). */
    installed: z.boolean(),
    /** The agent has a key from the manager. */
    enrolled: z.boolean(),
    /** `wazuh-agentd` is running, by its pid file. */
    running: z.boolean(),
    status: WazuhAgentStatus,
    /** As the state file writes them, local time without a zone: shown, not computed with. */
    last_keepalive: z.string().max(40).optional(),
    last_ack: z.string().max(40).optional(),
    /** W6: every line of `WAZUH_REMOTE_COMMAND_LOCKS` is in `local_internal_options.conf`. */
    remote_commands_locked: z.boolean(),
    /** The manager `ossec.conf` names, if Bartizo's client block is there. */
    manager_address: z.string().max(253).optional(),
});
/**
 * Which Wazuh this server runs, as `install.sh agent --wazuh-mode` chose (W10):
 * `central` is the Wazuh agent reporting to the central server's manager, `local`
 * P7's whole manager here. Recorded in `WAZUH_MODE_FILE` (`640 root:bartizo`),
 * which the agent reads to know which of its two Wazuh modules has work to do.
 */
export const WazuhMode = z.enum(['central', 'local']);
export const WAZUH_MODE_FILE = '/etc/bartizo/wazuh-mode';
/**
 * The block of the agent's `ossec.conf` that says where its manager is, written
 * by `enrol_wazuh_agent` and nothing else. Markers as for the FIM block: no
 * section tags and no double hyphens inside the comment.
 */
export const CLIENT_BLOCK_BEGIN = '<!-- BARTIZO CLIENT BEGIN: where this Wazuh agent enrols and reports, from the central server. Edits between these markers are replaced. -->';
export const CLIENT_BLOCK_BEGIN_PREFIX = '<!-- BARTIZO CLIENT BEGIN';
export const CLIENT_BLOCK_END = '<!-- BARTIZO CLIENT END -->';
/** Where the helper writes the manager's certificate on a server. */
export const WAZUH_MANAGER_CA_FILE = 'etc/bartizo-manager-ca.pem';
/** Where `wazuh-agentd` reads the enrolment password (relative to `/var/ossec`). */
export const WAZUH_AUTHD_PASS_FILE = 'etc/authd.pass';
/**
 * The client block, from validated arguments only: every value has passed a
 * schema that admits no `<`, `>`, `&` or quote. Enrolment is `wazuh-agentd`'s
 * own (the `<enrollment>` section): it enrols on its first start, and again by
 * itself if its key is ever lost.
 */
export function renderClientBlock(args, wazuhDir = '/var/ossec') {
    // Ending in a newline, as the FIM block does and as the installer keeps it.
    return `${[
        CLIENT_BLOCK_BEGIN,
        '<ossec_config>',
        '  <client>',
        '    <server>',
        `      <address>${args.manager_address}</address>`,
        `      <port>${String(WAZUH_AGENT_PORT)}</port>`,
        '      <protocol>tcp</protocol>',
        '    </server>',
        '    <notify_time>20</notify_time>',
        '    <time-reconnect>60</time-reconnect>',
        '    <auto_restart>yes</auto_restart>',
        '    <crypto_method>aes</crypto_method>',
        '    <enrollment>',
        '      <enabled>yes</enabled>',
        `      <manager_address>${args.manager_address}</manager_address>`,
        `      <port>${String(WAZUH_ENROLLMENT_PORT)}</port>`,
        `      <agent_name>${args.agent_name}</agent_name>`,
        `      <groups>${args.group}</groups>`,
        `      <server_ca_path>${wazuhDir}/${WAZUH_MANAGER_CA_FILE}</server_ca_path>`,
        `      <authorization_pass_path>${WAZUH_AUTHD_PASS_FILE}</authorization_pass_path>`,
        '    </enrollment>',
        '  </client>',
        '</ossec_config>',
        CLIENT_BLOCK_END,
    ].join('\n')}\n`;
}
/**
 * The agent's `wazuh_agent` module has no settings of its own in the config
 * document: it is on while file integrity monitoring is (`wazuh_fim.enabled`),
 * which `moduleSlices` hands it, so one switch in React still turns Wazuh's
 * part of a server off whichever mode it runs in.
 */
export const WazuhAgentSettings = z.object({ enabled: z.boolean().default(true) });
/** `inspect_wazuh_agent` takes nothing. */
export const InspectWazuhAgentArgs = z.object({}).strict();
/*
 * Each server's Wazuh agent as the central manager sees it (build plan P11.8.5):
 * read from the manager's API by `bartizo-api`, not reported by the server, so a
 * server whose Wazuh agent was stopped — by a fault or by somebody covering their
 * tracks — cannot say it is fine.
 */
/**
 * The manager's own words for an agent (`active`, `disconnected`,
 * `never_connected`, `pending`), and `not_enrolled`: the server has a group on
 * the manager (it asked to enrol, P11.8.2) and no agent of that name.
 */
export const WazuhAgentConnection = z.enum([
    'active',
    'disconnected',
    'never_connected',
    'pending',
    'not_enrolled',
]);
/** The states a person should look at, once they have lasted `WAZUH_AGENT_GRACE_MS`. */
export const WazuhAgentProblem = z.enum(['disconnected', 'never_connected', 'not_enrolled']);
/**
 * How long a problem lasts before it is one. Each new `agent.conf` restarts the
 * agent by itself (≈ 31 s, P11.8.3), during which the manager says
 * `disconnected`, and enrolment takes a few seconds: neither should put a server
 * on the overview's list. A disconnection counts from the agent's last
 * keep-alive (`wazuhAgentQuietSince`).
 */
export const WAZUH_AGENT_GRACE_MS = 2 * 60 * 1000;
/** Older than this, what the API last read is not trusted to put anything on a list. */
export const WAZUH_AGENT_STALE_MS = 5 * 60 * 1000;
/** `GET /v1/servers/:id`'s `wazuhAgent`. */
export const WazuhAgentHealth = z.object({
    connection: WazuhAgentConnection,
    /** The manager's id for it (`001` …); null when it is not enrolled. */
    agentId: z
        .string()
        .regex(/^\d{3,8}$/)
        .nullable(),
    /** As the agent reports it, `Wazuh v4.14.8`. */
    version: z.string().max(64).nullable(),
    lastKeepAlive: IsoTime.nullable(),
    registeredAt: IsoTime.nullable(),
    /** When the API first saw this connection state. */
    since: IsoTime,
    checkedAt: IsoTime,
    /** What the API last read is older than `WAZUH_AGENT_STALE_MS`: the manager's API is not answering. */
    stale: z.boolean(),
    /** The state a person should look at, once past the grace and while fresh; null when fine. */
    problem: WazuhAgentProblem.nullable(),
});
/**
 * When the current problem began: for a disconnection, the agent's last
 * keep-alive when that is earlier than the API first saw it, since the manager
 * says `disconnected` only `agents_disconnection_time` after the agent went quiet.
 */
export function wazuhAgentQuietSince(state) {
    if (state.connection !== 'disconnected' || state.lastKeepAlive === null)
        return state.since;
    return state.lastKeepAlive.getTime() < state.since.getTime() ? state.lastKeepAlive : state.since;
}
/** Whether `connection`, held since `since` and read at `checkedAt`, needs a person at `now`. */
export function wazuhAgentProblem(state, now) {
    const problem = WazuhAgentProblem.safeParse(state.connection);
    if (!problem.success)
        return null;
    if (now.getTime() - state.checkedAt.getTime() > WAZUH_AGENT_STALE_MS)
        return null;
    if (now.getTime() - wazuhAgentQuietSince(state).getTime() < WAZUH_AGENT_GRACE_MS)
        return null;
    return problem.data;
}
//# sourceMappingURL=wazuh-agent.js.map